import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import CityAutocomplete from '@/components/CityAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateRequestModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    city: '',
    check_in_date: '',
    check_out_date: '',
    guests: '',
    budget_min: '',
    budget_max: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Validate city is selected
      if (!formData.city || formData.city.trim() === '') {
        toast.error('Seleziona una città dalla lista');
        setLoading(false);
        return;
      }
      
      // Validate budget range
      const min = parseFloat(formData.budget_min);
      const max = parseFloat(formData.budget_max);
      
      if (min >= max) {
        toast.error('Il budget minimo deve essere inferiore al budget massimo');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API}/travel_requests/create`,
        {
          city: formData.city,
          check_in_date: formData.check_in_date,
          check_out_date: formData.check_out_date,
          guests: parseInt(formData.guests),
          budget_min: min,
          budget_max: max,
          notes: formData.notes || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(' Richiesta creata con successo! I property owners potranno farvi offerte.');
      setFormData({
        city: '',
        check_in_date: '',
        check_out_date: '',
        guests: '',
        budget_min: '',
        budget_max: '',
        notes: ''
      });
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating request:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione della richiesta';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-2xl font-bold text-slate-900">Crea Nuova Richiesta</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* City */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Città *
              </label>
              <CityAutocomplete
                value={formData.city}
                onChange={(city) => setFormData({ ...formData, city })}
                placeholder="Cerca città..."
              />
              {formData.city && (
                <p className="text-xs text-emerald-600 mt-1">✓ Città selezionata: {formData.city}</p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Check-in *
                </label>
                <Input
                  required
                  type="date"
                  value={formData.check_in_date}
                  onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Check-out *
                </label>
                <Input
                  required
                  type="date"
                  value={formData.check_out_date}
                  onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                  min={formData.check_in_date || new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
            </div>

            {/* Guests */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Numero Ospiti *
              </label>
              <Input
                required
                type="number"
                min="1"
                max="20"
                value={formData.guests}
                onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                placeholder="es. 2"
                className="w-full"
              />
            </div>

            {/* Budget Range */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Range di Budget (per notte)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Budget Minimo *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="1"
                      value={formData.budget_min}
                      onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                      placeholder="50"
                      className="w-full pl-8"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Budget Massimo *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">€</span>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="1"
                      value={formData.budget_max}
                      onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                      placeholder="80"
                      className="w-full pl-8"
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2">
                I property owners potranno farvi offerte tra questi due valori
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Note / Preferenze
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="es. Preferisco strutture vicino al centro, animali ammessi..."
                className="w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                disabled={loading}
              >
                {loading ? 'Creazione...' : 'Crea Richiesta'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRequestModal;
